import React from 'react';
import { Buildings, User } from '@phosphor-icons/react';

/**
 * Shared UI atoms for "wlasnosc" (ownership) of a Pojazd.
 * Values: 'firmowy' (company car) | 'prywatny' (privately owned).
 * Undefined/missing defaults to 'firmowy' (legacy pojazdy).
 */

export const WLASNOSC_OPTIONS = [
  { value: 'firmowy', label: 'Firmowy', short: 'F', icon: Buildings, color: 'blue' },
  { value: 'prywatny', label: 'Prywatny', short: 'P', icon: User, color: 'amber' },
];

export function normalizeWlasnosc(v) {
  // Case-insensitive prefix match: catches "prywatny", "Prywatny",
  // "prywatne", "prywatna" (mongo docs may have any of these forms).
  if (typeof v === 'string' && v.toLowerCase().startsWith('pryw')) return 'prywatny';
  return 'firmowy';
}

/** Small badge shown next to a vehicle name. */
export function WlasnoscBadge({ value, size = 'sm' }) {
  const w = normalizeWlasnosc(value);
  const opt = WLASNOSC_OPTIONS.find((o) => o.value === w);
  const Icon = opt.icon;
  const cls = {
    firmowy: 'bg-blue-100 text-blue-700 border-blue-200',
    prywatny: 'bg-amber-100 text-amber-700 border-amber-200',
  }[w];
  const sz = size === 'xs'
    ? 'px-1.5 py-0.5 text-[9px] gap-0.5'
    : 'px-2 py-0.5 text-[10px] gap-1';
  return (
    <span
      className={`inline-flex items-center rounded border font-bold uppercase tracking-wide ${cls} ${sz}`}
      data-testid={`wlasnosc-badge-${w}`}
      title={`Pojazd ${opt.label.toLowerCase()}`}
    >
      <Icon className="w-3 h-3" weight="bold" />
      {size === 'xs' ? opt.short : opt.label}
    </span>
  );
}

/**
 * Filter pill group: [Wszystkie | Firmowe | Prywatne].
 * `value` is one of 'all' | 'firmowy' | 'prywatny'; onChange receives new value.
 * `counts` optional: { firmowy: N, prywatny: N, all: N } to show per-tab totals.
 */
export function WlasnoscFilter({ value = 'all', onChange, counts, className = '', testIdPrefix = 'wlasnosc-filter' }) {
  const items = [
    { value: 'all', label: 'Wszystkie', bg: 'bg-slate-700', border: 'border-slate-700' },
    { value: 'firmowy', label: 'Firmowe', bg: 'bg-blue-600', border: 'border-blue-600' },
    { value: 'prywatny', label: 'Prywatne', bg: 'bg-amber-500', border: 'border-amber-500' },
  ];
  return (
    <div className={`inline-flex rounded-lg border border-slate-200 bg-white p-0.5 ${className}`}>
      {items.map((it) => {
        const active = value === it.value;
        const n = counts ? counts[it.value] ?? 0 : null;
        return (
          <button
            key={it.value}
            type="button"
            onClick={() => onChange(it.value)}
            data-testid={`${testIdPrefix}-${it.value}`}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${
              active ? `${it.bg} text-white shadow-sm` : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {it.label}
            {n !== null && (
              <span className={`ml-1.5 inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded text-[9px] font-bold ${
                active ? 'bg-white/25' : 'bg-slate-200 text-slate-600'
              }`}>
                {n}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Utility: apply a wlasnosc filter to any array of pojazdy or objects w/ wlasnosc field. */
export function applyWlasnoscFilter(items, filterValue, pojazdyById) {
  if (!filterValue || filterValue === 'all') return items;
  return items.filter((it) => {
    // Direct wlasnosc field, or lookup by pojazd_id in the map
    const w = normalizeWlasnosc(
      it.wlasnosc ?? (pojazdyById && it.pojazd_id ? pojazdyById[it.pojazd_id]?.wlasnosc : undefined)
    );
    return w === filterValue;
  });
}
